import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  applyActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from "firebase/auth";
import { auth } from "@/lib/firebase";
import { useTranslations } from "@/contexts/I18nContext";
import { userFacingMessage } from "@/lib/errors/userFacingError";
import {
  decideAuthAction,
  normalizeMode,
} from "@/lib/authActionRouting";
import {
  MIN_PASSWORD_LENGTH,
  validateNewPassword,
} from "@/lib/passwordValidation";

type Phase =
  | { kind: "loading" }
  | { kind: "form"; mode: "resetPassword"; email: string }
  | { kind: "success"; mode: "resetPassword" | "verifyEmail" }
  | { kind: "error"; message: string };

export function AuthActionPage() {
  const [params] = useSearchParams();
  const t = useTranslations();

  const mode = normalizeMode(params.get("mode"));
  const oobCode = params.get("oobCode");

  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const decision = decideAuthAction(mode, oobCode);
    if (decision.kind === "missing-code") {
      setPhase({ kind: "error", message: t("authAction.errors.invalidLink") });
      return;
    }
    if (decision.kind === "unsupported") {
      setPhase({ kind: "error", message: t("authAction.errors.unsupportedMode") });
      return;
    }

    // From here on `oobCode` is guaranteed truthy by decideAuthAction.
    const code = oobCode as string;
    let cancelled = false;
    (async () => {
      try {
        if (decision.kind === "needs-verify-reset") {
          const email = await verifyPasswordResetCode(auth, code);
          if (!cancelled) setPhase({ kind: "form", mode: "resetPassword", email });
        } else {
          await applyActionCode(auth, code);
          if (!cancelled) setPhase({ kind: "success", mode: "verifyEmail" });
        }
      } catch (err) {
        if (!cancelled) {
          setPhase({
            kind: "error",
            message: userFacingMessage(err, t("authAction.errors.linkExpired")),
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mode, oobCode, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (phase.kind !== "form" || !oobCode) return;

    setFormError("");
    const validation = validateNewPassword(newPassword, confirmPassword);
    if (!validation.ok) {
      setFormError(t(`authAction.errors.${validation.errorKey}`));
      return;
    }

    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setPhase({ kind: "success", mode: "resetPassword" });
    } catch (err) {
      setFormError(userFacingMessage(err, t("authAction.errors.resetFailed")));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-sm">
      <div className="text-center mb-8">
        <h1
          className="text-3xl font-bold"
          style={{ color: "var(--accent-color, #00C7BE)" }}
        >
          Zoey
        </h1>
        <p className="text-gray-500 mt-2">
          {phase.kind === "form"
            ? t("authAction.resetPassword.subtitle")
            : phase.kind === "success" && phase.mode === "resetPassword"
              ? t("authAction.resetPassword.successSubtitle")
              : phase.kind === "success" && phase.mode === "verifyEmail"
                ? t("authAction.verifyEmail.successSubtitle")
                : t("authAction.subtitleNeutral")}
        </p>
      </div>

      {phase.kind === "loading" && (
        <div className="flex flex-col items-center gap-3 py-6 text-sm text-gray-500 dark:text-gray-400">
          <div
            className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
            style={{
              borderColor: "var(--accent-color, #00C7BE)",
              borderTopColor: "transparent",
            }}
          />
          {t("authAction.loading")}
        </div>
      )}

      {phase.kind === "error" && (
        <div className="space-y-4">
          <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
            {phase.message}
          </div>
          <Link
            to="/login"
            className="block w-full py-2.5 rounded-lg text-white font-semibold text-center transition-opacity hover:opacity-90"
            style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
          >
            {t("authAction.requestNew")}
          </Link>
        </div>
      )}

      {phase.kind === "form" && (
        <form onSubmit={handleSubmit} className="space-y-4">
          {formError && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
              {formError}
            </div>
          )}

          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("authAction.resetPassword.emailLabel", { email: phase.email })}
          </p>

          <div>
            <label htmlFor="new-password" className="block text-sm font-medium mb-1">
              {t("authAction.resetPassword.newPasswordLabel")}
            </label>
            <input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={MIN_PASSWORD_LENGTH}
              autoComplete="new-password"
              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color,#00C7BE)]"
              placeholder="••••••••"
            />
          </div>

          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium mb-1">
              {t("authAction.resetPassword.confirmLabel")}
            </label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={MIN_PASSWORD_LENGTH}
              autoComplete="new-password"
              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color,#00C7BE)]"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-2.5 rounded-lg text-white font-semibold transition-opacity disabled:opacity-50"
            style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
          >
            {submitting
              ? t("authAction.resetPassword.submitting")
              : t("authAction.resetPassword.submit")}
          </button>
        </form>
      )}

      {phase.kind === "success" && (
        <div className="space-y-4 text-center">
          <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-sm">
            {phase.mode === "resetPassword"
              ? t("authAction.resetPassword.success")
              : t("authAction.verifyEmail.success")}
          </div>
          <Link
            to="/login"
            className="block w-full py-2.5 rounded-lg text-white font-semibold transition-opacity hover:opacity-90"
            style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
          >
            {t("authAction.signIn")}
          </Link>
        </div>
      )}
    </div>
  );
}
